import { AfterViewInit, Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { filter, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subscription } from 'rxjs';
import { FulfillmentOrder, FulfillmentOrderStatus } from '@vendasta/order-fulfillment';
import {
  StatusBadgeComponent,
  WorkOrderFormComponent,
  WorkOrderFormService,
  WorkOrderModule,
  WorkOrderPersona,
  WorkOrderUnsavedChangesComponent,
  WorkOrderUnsavedChangesDialogComponent,
  WorkOrderUnsavedChangesDialogResult,
  clearUnsavedChangesFromSessionStorage,
  doesWorkOrderFormHaveUnsavedChanges,
  getTemporaryStorageKey,
} from '@vendasta/work-order';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { CommonModule } from '@angular/common';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

@Component({
  imports: [
    WorkOrderModule,
    WorkOrderFormComponent,
    GalaxyAlertModule,
    TranslateModule,
    GalaxyPageModule,
    CommonModule,
    StatusBadgeComponent,
  ],
  selector: 'bc-public-fulfillment-form',
  templateUrl: './public-fulfillment-form.component.html',
  styleUrls: ['./public-fulfillment-form.component.scss'],
})
export class PublicFulfillmentFormComponent
  extends WorkOrderUnsavedChangesComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  salesOrderId$: Observable<string>;
  accountGroupId$: Observable<string>;
  workOrders$$: BehaviorSubject<FulfillmentOrder[]> = new BehaviorSubject<FulfillmentOrder[]>(null);
  workOrders$: Observable<FulfillmentOrder[]> = this.workOrders$$.asObservable().pipe(filter((val) => !!val));
  isWorkOrderLoading$$: ReplaySubject<boolean> = new ReplaySubject(1);
  workOrdersCompleted = '';
  partnerLogoUrl$: Observable<string>;
  showBanner = false;

  persona = WorkOrderPersona.SMB;
  subscriptions: Subscription[] = [];

  private woFormDoneLoadingMap$$: BehaviorSubject<Map<number, boolean>> = new BehaviorSubject(
    new Map<number, boolean>(),
  );
  woFormDoneLoading$: Observable<boolean>;

  @ViewChildren(WorkOrderFormComponent) woFormComponents: QueryList<WorkOrderFormComponent>;
  private combinedFormGroup: UntypedFormGroup = new UntypedFormGroup({});

  constructor(
    private route: ActivatedRoute,
    private readonly i18n: TranslateService,
    private workOrderFormService: WorkOrderFormService,
    private dialog: MatDialog,
  ) {
    super();
  }

  ngOnInit(): void {
    this.salesOrderId$ = this.route.paramMap.pipe(
      map((paramMap) => paramMap.get('orderId')),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.accountGroupId$ = this.route.paramMap.pipe(
      map((paramMap) => paramMap.get('accountGroupId')),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.subscriptions.push(
      combineLatest([this.salesOrderId$, this.accountGroupId$])
        .pipe(
          tap(() => this.isWorkOrderLoading$$.next(true)),
          switchMap(([salesOrderId, agid]) =>
            this.workOrderFormService.getOrdersToRenderFormsFor(salesOrderId, agid, true),
          ),
          tap(() => this.isWorkOrderLoading$$.next(false)),
          finalize(() => this.isWorkOrderLoading$$.next(false)),
        )
        .subscribe((workOrders) => {
          this.workOrders$$.next(workOrders);
          workOrders.forEach((_, i) => {
            const loadingMap: Map<number, boolean> = this.woFormDoneLoadingMap$$.getValue();
            loadingMap.set(i, false);
            this.woFormDoneLoadingMap$$.next(loadingMap);
          });
        }),
    );

    this.partnerLogoUrl$ = this.workOrders$.pipe(
      filter((workOrders) => workOrders?.length > 0),
      map((workOrders) => workOrders[0]),
      map((workOrder) => workOrder?.businessInfo?.partnerLogoUrl),
    );

    this.woFormDoneLoading$ = this.woFormDoneLoadingMap$$.pipe(
      map((woLoadingMap) => {
        const loadingValues: boolean[] = Array.from(woLoadingMap.values());
        return loadingValues?.length > 0 && loadingValues.every((value) => value);
      }),
    );
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.woFormComponents.changes.subscribe((workOrderForms) => {
        workOrderForms.forEach((woFormComp) => {
          const key: string = getTemporaryStorageKey(woFormComp.accountGroupId, woFormComp.appId);
          if (!this.combinedFormGroup.contains(key)) {
            this.combinedFormGroup.setControl(key, woFormComp.form);
          }
        });
      }),
    );
  }

  canDeactivate(): boolean {
    return !doesWorkOrderFormHaveUnsavedChanges(this.combinedFormGroup);
  }

  pendingChangesDialog(): boolean | Observable<boolean> {
    const openDialogs = this.dialog.openDialogs || [];
    if (openDialogs.length === 0) {
      return this.dialog
        .open(WorkOrderUnsavedChangesDialogComponent)
        .afterClosed()
        .pipe(
          map((result: WorkOrderUnsavedChangesDialogResult) => {
            return result === WorkOrderUnsavedChangesDialogResult.CONTINUE_EDITING ? false : !!result;
          }),
          tap((result: boolean) => {
            if (result) {
              clearUnsavedChangesFromSessionStorage(this.combinedFormGroup);
            }
          }),
        );
    }
    return false;
  }

  //This will only get emitted if we have a work order, so it won't be called if we have submission on its own.
  statusChanged(newStatus: FulfillmentOrderStatus, wo: FulfillmentOrder): void {
    const actualWorkOrders: FulfillmentOrder[] = this.workOrders$$.getValue();
    const index: number = actualWorkOrders.findIndex((w) => w.fulfillmentOrderId === wo.fulfillmentOrderId);
    if (index > -1) {
      actualWorkOrders[index].status = newStatus;
      this.workOrders$$.next(actualWorkOrders);
    }
    this.calculateCompleteCount(actualWorkOrders);
  }

  calculateCompleteCount(fulfillmentOrders: FulfillmentOrder[]): void {
    const totalOrders: number = fulfillmentOrders?.length || 0;
    const completedOrders: number =
      fulfillmentOrders.filter((wo) => wo.status !== FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_DETAILS_NEEDED)
        .length || 0;
    this.showBanner = completedOrders < totalOrders;
    this.workOrdersCompleted =
      completedOrders + '/' + totalOrders + ' ' + this.i18n.instant('WORK_ORDERS.PUBLIC_FORM.COMPLETED');
  }

  handleDoneLoading(index: number): void {
    const loadingMap: Map<number, boolean> = this.woFormDoneLoadingMap$$.getValue();
    loadingMap.set(index, true);
    this.woFormDoneLoadingMap$$.next(loadingMap);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
